import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { BookOpen, Printer, User, Wrench, ShieldStar } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

const ROLE_META = {
  client:     { label: "Client",       icon: User,       color: "text-blue-500" },
  technicien: { label: "Technicien",   icon: Wrench,     color: "text-emerald-500" },
  admin:      { label: "Administrateur", icon: ShieldStar, color: "text-amber-500" },
};

// Slugify a heading text for anchor generation (French-safe: strip diacritics)
const slugify = (text) =>
  String(text).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Component defined at module scope to avoid React "Unstable Nested Components" warning
const MarkdownH2 = ({ children, ...props }) => (
  <h2 id={slugify(children)} {...props}>{children}</h2>
);

const MARKDOWN_COMPONENTS = { h2: MarkdownH2 };

export default function Documentation({ embedded = false }) {
  const { user } = useAuth();
  const [available, setAvailable] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/documentation").then((r) => {
      setAvailable(r.data || []);
      // Default: current user's own guide, or first available
      const own = (r.data || []).find((x) => x.role === user?.role);
      setActiveRole(own?.role || r.data?.[0]?.role || null);
    }).catch(() => setAvailable([]));
  }, [user?.role]);

  useEffect(() => {
    if (!activeRole) return;
    setLoading(true);
    api.get(`/documentation/${activeRole}`)
      .then((r) => setDoc(r.data))
      .catch(() => setDoc(null))
      .finally(() => setLoading(false));
  }, [activeRole]);

  const handlePrint = () => {
    // Add a print-only class so the @media print rules kick in
    document.body.classList.add("printing-doc");
    // Give the browser a tick to apply CSS before opening the print dialog
    requestAnimationFrame(() => {
      window.print();
      // Cleanup after the dialog closes (afterprint fires reliably in modern browsers)
      const cleanup = () => {
        document.body.classList.remove("printing-doc");
        window.removeEventListener("afterprint", cleanup);
      };
      window.addEventListener("afterprint", cleanup);
      // Fallback in case afterprint doesn't fire (Safari quirks)
      setTimeout(cleanup, 8000);
    });
  };

  return (
    <div className={embedded ? "space-y-6 doc-container p-6" : "space-y-6 doc-container"}>
      <header className="flex flex-wrap items-end justify-between gap-4 no-print">
        <div>
          {!embedded && <div className="label-caps">Aide</div>}
          <h1 className={`font-display ${embedded ? "text-2xl" : "text-3xl md:text-4xl"} font-bold mt-1 flex items-center gap-3`}>
            <BookOpen size={embedded ? 24 : 32} weight="duotone" className="text-primary"/> Documentation
          </h1>
          <p className="text-muted-foreground mt-2">
            Guides détaillés du fonctionnement de MasterTicket — captures d&apos;écran, procédures, cas d&apos;usage.
          </p>
        </div>
        <Button onClick={handlePrint} data-testid="doc-print" variant="secondary">
          <Printer size={16} className="mr-2"/> Imprimer / Enregistrer en PDF
        </Button>
      </header>

      {available.length > 1 && (
        <Card className="no-print">
          <CardContent className="pt-5">
            <div className="label-caps mb-3">Consulter en tant que</div>
            <div className="flex flex-wrap gap-2">
              {available.map((d) => {
                const meta = ROLE_META[d.role] || {};
                const Icon = meta.icon;
                const active = activeRole === d.role;
                return (
                  <button key={d.role}
                    onClick={() => setActiveRole(d.role)}
                    data-testid={`doc-tab-${d.role}`}
                    className={`px-4 py-2 rounded-lg border text-sm inline-flex items-center gap-2 transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground border-primary shadow-sm"
                        : "bg-background hover:bg-muted border-border text-foreground"
                    }`}>
                    {Icon && <Icon size={16} weight={active ? "fill" : "regular"} className={active ? "" : meta.color}/>}
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="doc-content-card">
        <CardContent className="pt-8 pb-8 px-6 md:px-10">
          {loading && <div className="text-muted-foreground text-center py-10">Chargement du guide…</div>}
          {!loading && !doc && <div className="text-muted-foreground text-center py-10">Guide indisponible.</div>}
          {!loading && doc && (
            <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-8">
              {/* Sommaire cliquable auto-généré */}
              <aside className="no-print lg:sticky lg:top-4 lg:self-start">
                <div className="label-caps mb-2">Sommaire</div>
                <nav className="space-y-1 text-sm border-l-2 pl-3">
                  {(doc.content.match(/^## .+$/gm) || []).map((h, i) => {
                    const label = h.replace(/^## /, "");
                    return (
                      <a key={i} href={`#${slugify(label)}`} data-testid={`doc-toc-${i}`}
                         className="block text-muted-foreground hover:text-primary hover:border-primary transition-colors py-0.5">
                        {label}
                      </a>
                    );
                  })}
                </nav>
              </aside>
              <article className="doc-markdown prose prose-slate dark:prose-invert max-w-none min-w-0 overflow-hidden" data-testid="doc-body">
                <ReactMarkdown components={MARKDOWN_COMPONENTS}>{doc.content}</ReactMarkdown>
                <div className="mt-10 pt-6 border-t text-[0.7rem] text-muted-foreground text-center no-print">
                  MasterTicket / Helios — Documentation générée le {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}
                </div>
              </article>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
